import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, X, WhatsappLogo, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import useBackGuard from '../hooks/useBackGuard';

const RIJSCHOOL_WA = '31620933773'; // synced with RijschoolInfo.js
const DISMISS_KEY = 'referral_gift_dismissed_until';
const ANIM_KEY = 'referral_gift_anim_count';
const FIRST_ANIM_DELAY_MS = 45 * 1000;  // 45s after mount
const SECOND_ANIM_DELAY_MS = 4 * 60 * 1000;  // ~4 min later
const DISMISS_COOLDOWN_DAYS = 3;

function buildWhatsAppLink(currentName, friendName, friendPhone) {
    const lines = [
        '🎁 Hoi! Ik heb iemand voor de gratis-lesactie van Rijschool AutoPilot.',
        '',
        currentName ? `Aangedragen door: ${currentName}` : null,
        `Nieuwe leerling: ${friendName}`,
        `Telefoon: ${friendPhone}`,
        '',
        'Kunnen jullie contact opnemen voor een proefles / pakket?',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${RIJSCHOOL_WA}?text=${encodeURIComponent(lines)}`;
}

export default function ReferralGift({ studentName }) {
    const [open, setOpen] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [shaking, setShaking] = useState(false);
    const [friendName, setFriendName] = useState('');
    const [friendPhone, setFriendPhone] = useState('');
    const [error, setError] = useState('');
    const animCountRef = useRef(0);

    // Decide visibility based on dismissal cooldown
    useEffect(() => {
        try {
            const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
            if (Date.now() >= until) setShouldRender(true);
        } catch {
            setShouldRender(true);
        }
    }, []);

    // Listen for a "open referral" event coming from the chat quick replies
    useEffect(() => {
        const open = () => {
            setShouldRender(true);
            setOpen(true);
        };
        window.addEventListener('qr:open-referral', open);
        return () => window.removeEventListener('qr:open-referral', open);
    }, []);

    // Android back-gesture: close referral modal instead of exiting the PWA
    useBackGuard(open, () => setOpen(false));

    // Schedule the subtle attention animation (max 2x per session)
    useEffect(() => {
        if (!shouldRender) return;
        const initial = parseInt(sessionStorage.getItem(ANIM_KEY) || '0', 10);
        animCountRef.current = initial;

        const playShake = () => {
            if (animCountRef.current >= 2) return;
            setShaking(true);
            window.setTimeout(() => setShaking(false), 1100);
            animCountRef.current += 1;
            sessionStorage.setItem(ANIM_KEY, String(animCountRef.current));
        };

        const t1 = window.setTimeout(playShake, FIRST_ANIM_DELAY_MS);
        const t2 = window.setTimeout(playShake, FIRST_ANIM_DELAY_MS + SECOND_ANIM_DELAY_MS);
        return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
    }, [shouldRender]);

    const handleDismiss = () => {
        // Remember dismissal for a few days
        try {
            const until = Date.now() + DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
            localStorage.setItem(DISMISS_KEY, String(until));
        } catch { /* ignore */ }
        setShouldRender(false);
        setOpen(false);
    };

    const handleSubmit = (e) => {
        e?.preventDefault?.();
        setError('');
        const name = friendName.trim();
        const phone = friendPhone.replace(/\s+/g, '').trim();
        if (!name) { setError('Vul de naam van je vriend(in) in.'); return; }
        if (!phone || phone.length < 8) { setError('Vul een geldig telefoonnummer in.'); return; }
        const url = buildWhatsAppLink(studentName, name, phone);
        // Open in new tab (popup-blocker-friendly since triggered by click)
        window.open(url, '_blank', 'noopener,noreferrer');
        // Soft-close + remember for 3 days so user isn't pestered
        try {
            const until = Date.now() + DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
            localStorage.setItem(DISMISS_KEY, String(until));
        } catch { /* ignore */ }
        setOpen(false);
        setFriendName('');
        setFriendPhone('');
    };

    if (!shouldRender) return null;

    return (
        <>
            {/* Floating trigger button */}
            <motion.button
                onClick={() => setOpen(true)}
                animate={shaking ? { rotate: [0, -6, 6, -4, 4, -2, 2, 0], y: [0, -2, 0, -1, 0] } : { rotate: 0, y: 0 }}
                transition={shaking ? { duration: 1.0, ease: 'easeInOut' } : { duration: 0.2 }}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                className="fixed bottom-20 right-4 sm:bottom-20 sm:right-6 z-[60] inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-br from-[#D4A537] to-[#B88A24] text-[#1A202C] shadow-[0_8px_24px_-6px_rgba(212,165,55,0.55)] ring-1 ring-[#E5B84A]/40 backdrop-blur-sm hover:shadow-[0_12px_28px_-6px_rgba(212,165,55,0.7)] transition-shadow"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="referral-gift-fab"
                aria-label="Verdien een gratis rijles"
            >
                <Gift size={20} weight="fill" />
                <span className="text-sm font-semibold">Gratis les</span>
            </motion.button>

            {/* Modal */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setOpen(false)}
                        data-testid="referral-gift-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.94, y: 24, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.94, y: 24, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-md bg-[#2D3748] border border-[#4A5568] rounded-2xl shadow-2xl overflow-hidden"
                        >
                            {/* Decorative gradient header */}
                            <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-[#D4A537]/20 via-[#2D3748] to-[#2D3748] border-b border-[#4A5568]">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2.5 rounded-xl bg-[#D4A537]/20 border border-[#D4A537]/40 text-[#D4A537]">
                                            <Gift size={22} weight="fill" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg sm:text-xl font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                Verdien een gratis rijles
                                            </h2>
                                            <p className="text-xs text-[#D4A537]/90 mt-0.5 flex items-center gap-1">
                                                <Sparkle size={12} weight="fill" /> AutoPilot beloning
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="text-gray-400 hover:text-white transition-colors p-1 -m-1"
                                        data-testid="referral-gift-close"
                                        aria-label="Sluiten"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="px-5 sm:px-6 py-5">
                                <p className="text-sm text-gray-300 leading-relaxed">
                                    Ken je iemand die ook rijlessen wil volgen? Als jouw vriend of vriendin een lespakket afneemt, krijg jij <span className="text-[#D4A537] font-semibold">een gratis rijles</span> cadeau 🚗✨
                                </p>

                                <form onSubmit={handleSubmit} className="mt-5 space-y-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs uppercase tracking-wider text-gray-400">Naam nieuwe leerling</Label>
                                        <Input
                                            type="text"
                                            value={friendName}
                                            onChange={(e) => setFriendName(e.target.value)}
                                            placeholder="bv. Lisa Janssen"
                                            className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus-visible:ring-[#D4A537]"
                                            data-testid="referral-friend-name"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs uppercase tracking-wider text-gray-400">Telefoonnummer</Label>
                                        <Input
                                            type="tel"
                                            inputMode="tel"
                                            value={friendPhone}
                                            onChange={(e) => setFriendPhone(e.target.value)}
                                            placeholder="06-12345678"
                                            className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus-visible:ring-[#D4A537]"
                                            data-testid="referral-friend-phone"
                                        />
                                    </div>

                                    {error && (
                                        <p className="text-xs text-red-400" data-testid="referral-error">{error}</p>
                                    )}

                                    <Button
                                        type="submit"
                                        className="w-full bg-[#25D366] hover:bg-[#1FBE5C] text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                                        data-testid="referral-submit"
                                    >
                                        <WhatsappLogo size={18} weight="fill" />
                                        Verstuur via WhatsApp
                                    </Button>
                                </form>

                                <button
                                    onClick={handleDismiss}
                                    className="mt-3 w-full text-xs text-gray-500 hover:text-gray-300 transition-colors"
                                    data-testid="referral-snooze"
                                >
                                    Even niet — herinner me later
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
